/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { TopBar } from '@cooltrax/jellyfish-ui'
import { MdDashboard, MdOutlineDashboard } from 'react-icons/md'
import { TbTruck, TbBuilding, TbMapPin, TbFileText, TbBell, TbSettings, TbExternalLink } from 'react-icons/tb'
import { usePermissions } from '../../hooks/usePermissions'
import { CompanyDropdown, useAuth } from '@cooltrax/jellyfish-sdk'
import JellyfishTopBarMobile from './JellyfishTopBarMobile'

export const JellyfishTopbar: React.FC = () => {
  const { logout, isAuthenticated, token, user } = useAuth()
  const location = useLocation()
  const [currentTheme, setCurrentTheme] = useState<string>('cooltrax-light')
  const { hasUiDev, hasPermission, refreshPermissions } = usePermissions()

  const hasAuditAccess = hasPermission('audit:r') || hasPermission('audit:r*')
  const { VITE_FRESH_LEGACY_URL } = import.meta.env
  const hasInsights = hasPermission('insights:r')

  // Refresh permissions when token changes
  useEffect(() => {
    refreshPermissions()
  }, [token, refreshPermissions])

  // Use SDK login instead of manual redirect
  const handleLogin = useCallback(() => {
    window.location.href = '/login'
  }, [])

  const isActive = (path: string) => {
    return location.pathname.startsWith(path)
      ? 'bg-base-300 text-primary font-semibold shadow-sm'
      : 'text-base-content hover:text-primary'
  }

  // Function to determine logo path based on theme
  const getLogoPath = (theme: string) => {
    const isDarkTheme = theme.includes('dark') || theme === 'cooltrax-dark'
    return isDarkTheme ? '/logo-dark.png' : '/logo-light.png'
  }

  // Track theme changes
  useEffect(() => {
    const themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme') || 'cooltrax-light'
          setCurrentTheme(newTheme)
        }
      })
    })

    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    // Initial theme
    setCurrentTheme(document.documentElement.getAttribute('data-theme') || 'cooltrax-light')

    return () => themeObserver.disconnect()
  }, [])

  // Effect to monitor authentication status changes (perm refresh)
  useEffect(() => {
    refreshPermissions()
  }, [isAuthenticated, refreshPermissions])

  const handleLogout = () => {
    // Delegate to SDK logout. SDK should clear its own tokens/state.
    logout({ logoutParams: { returnTo: window.location.origin + '/fresh/' } })
  }

  const redirectToLegacyFresh = () => {
    if (!VITE_FRESH_LEGACY_URL) return

    const newWindow = window.open('', 'refreshInsightsTab')
    if (newWindow?.location.href === 'about:blank') {
      newWindow.location.href = VITE_FRESH_LEGACY_URL
    } else {
      newWindow?.focus()
    }
  }

  return (
    <TopBar
      title="Cooltrax"
      className="bg-base-200 shadow-xl border-b border-base-300 h-12 relative"
      logo={getLogoPath(currentTheme)}
      handleLogin={handleLogin}
      isAuthenticated={isAuthenticated}
      handleLogout={handleLogout}
      user={user ? user : undefined}
    >
      {/* Fresh (Legacy) link - positioned absolutely after logo */}
      {isAuthenticated && VITE_FRESH_LEGACY_URL && (
        <button
          onClick={redirectToLegacyFresh}
          className="absolute left-48 top-1/2 transform -translate-y-1/2 hidden lg:flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium text-base-content hover:text-primary border border-base-300 rounded-md z-10"
        >
          <TbExternalLink className="text-accent h-4 w-4" /> Fresh
        </button>
      )}

      {/* Mobile menu trigger */}
      <JellyfishTopBarMobile
        hasUiDev={hasUiDev}
        hasAuditAccess={hasAuditAccess}
        isAuthenticated={isAuthenticated}
        redirectToLegacyFresh={redirectToLegacyFresh}
        hasLegacyUrl={!!VITE_FRESH_LEGACY_URL}
        hasInsights={hasInsights}
      />

      {/* Desktop navigation menu */}
      <div className="hidden lg:flex items-center justify-end w-full gap-2">
        <ul className="hidden lg:flex menu menu-horizontal flex-nowrap gap-0.5">
          {/* Action icons */}
          <div className="flex items-center gap-1.5">
            {isAuthenticated && (
              <div className="mr-2">
                <CompanyDropdown size="sm" variant="outline" />
              </div>
            )}
          </div>
          {/* Main navigation items for ui:dev users */}
          {hasInsights && (
            <li>
              <Link
                to="/insights/dashboards/default"
                className={`${isActive('/insights')} flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium`}
              >
                <MdOutlineDashboard className="text-accent h-4 w-4" /> Insights
              </Link>
            </li>
          )}
          {hasUiDev && (
            <>
              <li>
                <Link
                  to="/fresh/in-transit"
                  className={`${isActive('/fresh/in-transit')} flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium`}
                >
                  <TbTruck className="text-accent h-4 w-4" /> In Transit
                </Link>
              </li>
              <li>
                <Link
                  to="/fresh/in-store"
                  className={`${isActive('/fresh/in-store')} flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium`}
                >
                  <TbBuilding className="text-accent h-4 w-4" /> In Store
                </Link>
              </li>
              <li>
                <Link
                  to="/fresh/geofences"
                  className={`${isActive('/fresh/geofences')} flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium`}
                >
                  <TbMapPin className="text-accent h-4 w-4" /> Geofences
                </Link>
              </li>
              <li>
                <Link
                  to="/fresh/reports"
                  className={`${isActive('/fresh/reports')} flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium`}
                >
                  <TbFileText className="text-accent h-4 w-4" /> Reports
                </Link>
              </li>
              <li>
                <Link
                  to="/fresh/alerts"
                  className={`${isActive('/fresh/alerts')} flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium`}
                >
                  <TbBell className="text-accent h-4 w-4" /> Alerts
                </Link>
              </li>
            </>
          )}

          {/* Admin link shown to users with ui:dev or audit access */}
          {(hasUiDev || hasAuditAccess) && (
            <li>
              <Link
                to="/fresh/admin"
                className={`${isActive('/fresh/admin')} flex items-center gap-2 hover:bg-base-300 px-3 py-1.5 text-sm font-medium`}
              >
                <TbSettings className="text-accent h-4 w-4" /> Admin
              </Link>
            </li>
          )}
        </ul>
      </div>
    </TopBar>
  )
}
